import GokuDataGrid from '@/components/GokuDataGrid'
import { MonthRange } from '@/components/MonthRangeSelector'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import { CreateServiceCostModalProps } from '@/modals/ServiceCostModals/CreateModal'
import { UpdateServiceCostModalProps } from '@/modals/ServiceCostModals/UpdateModal'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'
import Filters from '../../../Revenue/components/shared/Filters'
import { deleteServiceCost, listServiceCosts } from '../../services'
import { ServiceCost } from '../../types'
import { columns } from '../columns'

const today = dayjs()

const ServiceCostsList: React.FC = () => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: QUERY_KEYS.REPORTS_BI_SERVICE_COSTS,
    })
    const [filters, setFilters] = useState<MonthRange>({
        gte: today.startOf('year').format('YYYY-MM'),
        lte: today.endOf('year').format('YYYY-MM'),
    })

    // Fetch service costs
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_SERVICE_COSTS, filters],
        queryFn: () =>
            listServiceCosts({
                from_month: filters.gte,
                to_month: filters.lte,
            }),
        staleTime: 5 * 60 * 1000,
    })

    // Delete mutation
    const { mutateAsync: deleteCostMutation } = useMutationWithHandler(
        {
            mutationFn: deleteServiceCost,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.REPORTS_BI_SERVICE_COSTS,
                QUERY_KEYS.REPORTS_BI_REVENUE_COST,
            ],
            successMessage: t('delete-success'),
            errorMessage: t('something went wrong'),
            onSuccess: () => {
                refetchCurrent()
            },
        }
    )

    const handleDeleteClick = (cost: ServiceCost) => {
        setConfirmation({
            open: true,
            title: t('confirm_delete'),
            description: t('confirm_delete_service_cost_message'),
            onConfirm: () => handleDeleteCost(cost.id),
        })
    }

    const handleDeleteCost = async (id: number) => {
        try {
            await deleteCostMutation(id)
        } catch (error) {
            console.error('Failed to delete service cost:', error)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const handleOpenCreate = () => {
        setModal<CreateServiceCostModalProps>(
            'ServiceCostModals/CreateModal/index',
            {
                refetch: refetchCurrent,
            }
        )
    }

    const handleOpenEdit = (cost: ServiceCost) => {
        setModal<UpdateServiceCostModalProps>(
            'ServiceCostModals/UpdateModal/index',
            {
                serviceCost: cost,
                refetch: refetchCurrent,
            }
        )
    }

    // DataGrid actions
    const dataGridActions: IAction<ServiceCost>[] = [
        {
            onClick: handleOpenEdit,
            title: t('edit'),
            color: 'primary',
            icon: <EditIcons />,
        },
        {
            onClick: handleDeleteClick,
            title: t('delete'),
            color: 'error',
            icon: <DeleteIcons />,
        },
    ]

    return (
        <div className="space-y-2">
            <Filters
                onFiltersChange={setFilters}
                initialFilters={filters}
                excludeOptions={['sale_manager_codes', 'account_manager_codes']}
            />
            <GokuDataGrid
                apiCall={async () => ({
                    results: data || [],
                    total: data?.length || 0,
                })}
                columns={columns}
                actions={dataGridActions}
                dataGridProps={{
                    getRowId: (row) => row.id,
                    hideFooter: true,
                    hideFooterPagination: true,
                }}
                options={{
                    pageSize: 20,
                    title: (
                        <div className="flex justify-end p-2">
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                className="min-w-[200px]"
                                onClick={handleOpenCreate}
                                startIcon={<AddIcons />}
                            >
                                {t('create')}
                            </Button>
                        </div>
                    ),
                    showSearch: false,
                }}
                reactQueryKey={
                    `${QUERY_KEYS.REPORTS_BI_SERVICE_COSTS}` +
                    JSON.stringify(filters)
                }
            />
        </div>
    )
}

export default ServiceCostsList
